import React, { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ComboboxInput,
  Combobox as HeadlessCombobox,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react';
import Spinner from '@dashboard/components/Spinner';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import Label from '@dashboard/components/Form/components/Label';

const propTypes = {
  name: PropTypes.string,
  value: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  immediate: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.any,
  placeholder: PropTypes.string,
  onChangeInput: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  hasItems: PropTypes.bool.isRequired,
  noItemsMsg: PropTypes.string,
  fetchNextPage: PropTypes.func,
  showLabel: PropTypes.bool,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  onClear: PropTypes.func,
  showSearchIcon: PropTypes.bool,
};

const defaultProps = {
  value: '',
  noItemsMsg: 'No items found',
  showSearchIcon: true,
};

const Combobox = props => {
  const {
    onClose,
    immediate,
    value,
    onChange,
    placeholder,
    isError,
    isLoading,
    hasItems,
    children,
    onChangeInput,
    noItemsMsg,
    fetchNextPage,
    tooltip,
    label,
    showLabel,
    name,
    onClear,
    showSearchIcon,
  } = props;
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState();

  return (
    <Fragment>
      <HeadlessCombobox
        immediate={immediate ?? false}
        value={value?._id}
        onChange={value => {
          onChange(value);
          /**
           * We need to wait for the blur event to fire before closing the combobox after selection.
           * Otherwise, the comboboxoptions will not close.
           */
          setTimeout(() => {
            if (value !== null) inputRef.current?.blur();
          }, 0);
        }}
        onClose={onClose}
      >
        {showLabel && (
          <Label htmlFor={name} tooltip={tooltip}>
            {label}
          </Label>
        )}
        <div className="relative">
          <ComboboxInput
            ref={inputRef}
            className="flex justify-between items-center w-full rounded-sm shadow-input py-1.5 pr-10 border-0 focus:outline-none pointer-events-auto"
            aria-label={placeholder}
            displayValue={() => value?.name}
            onChange={onChangeInput}
            placeholder={placeholder}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
            }}
          />

          <div className="absolute inset-y-1 right-2 flex items-center bg-white pointer-events-auto">
            {!isFocused && inputRef.current?.value.trim().length > 0 && (
              <button
                onClick={e => {
                  onClear(e);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }}
                className="pr-2 text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}

            {showSearchIcon && <MagnifyingGlassIcon className="text-gray-300 w-5 h-5" />}
          </div>
        </div>

        <ComboboxOptions>
          <div className="relative">
            <div className="absolute bg-white border border-gray-200 rounded w-full z-10 max-h-72 overflow-auto shadow-sm mt-2">
              {(isError || (!isLoading && !hasItems)) && (
                <ComboboxOption className="p-2">{noItemsMsg}</ComboboxOption>
              )}

              <BottomScrollListener onBottom={fetchNextPage}>{children}</BottomScrollListener>

              {isLoading && (
                <div className="p-2">
                  <Spinner />
                </div>
              )}
            </div>
          </div>
        </ComboboxOptions>
      </HeadlessCombobox>
    </Fragment>
  );
};

Combobox.propTypes = propTypes;
Combobox.defaultProps = defaultProps;

export default Combobox;
